/**
 * The Requests tab with nothing open: every request for this client in one
 * table, the ones waiting on the attorney first. A row opens its page; the
 * last cell holds the one action that matters now (Review, Remind or Open).
 */
import {
  AtSign,
  ClipboardList,
  Copy,
  Eye,
  LayoutTemplate,
  Link2,
  MoreHorizontal,
  Plus,
  Sparkles,
  Trash2
} from "lucide-react";
import { useMemo, useState, type FC } from "react";

import type { OutgoingIntake } from "@/api/types/outgoingIntake";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  ClientTable,
  TABLE_TEXT,
  type ClientTableColumn
} from "@/features/clientProfile/components/table/ClientTable";
import { ClientTableRow } from "@/features/clientProfile/components/table/ClientTableRow";
import {
  ClientTableToolbar,
  ToolbarFilterPill
} from "@/features/clientProfile/components/table/ClientTableToolbar";
import {
  ClientTableCell,
  MutedCell,
  TitleCell
} from "@/features/clientProfile/components/table/cells";
import { cn } from "@/lib/utils";

import { useRequestsIde } from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";
import { RemindPopover } from "../editor/RemindPopover";
import { NewRequestDialog } from "../explorer/NewRequestDialog";
import { NewRequestMenu } from "../explorer/NewRequestMenu";
import { useNewRequest } from "../explorer/useNewRequest";
import { displayStatus, intakeArtifact, previewArtifact } from "../model";
import { useIntakeActions } from "../useIntakeActions";
import { useOpenRequest } from "../useOpenRequest";
import {
  emptyMatchTitle,
  lastActivity,
  progressOf,
  REQUESTS_FILTERS,
  rowAction,
  shortDate,
  starterPrompts,
  statusTone,
  visibleRequests,
  type RequestsFilter
} from "./listModel";

const COLUMNS: ClientTableColumn[] = [
  { id: "request", header: "Request", style: { width: "34%" } },
  { id: "status", header: "Status" },
  { id: "progress", header: "Progress" },
  { id: "sent", header: "Sent" },
  { id: "activity", header: "Last Activity" },
  { id: "action", header: "", style: { width: 140 } }
];

type Props = {
  clientName: string | null;
};

const ProgressCell: FC<{ intake: OutgoingIntake }> = ({ intake }) => {
  const progress = progressOf(intake);
  if (progress.kind === "size") return <MutedCell>{progress.text}</MutedCell>;
  const { answered, total } = progress;
  return (
    <ClientTableCell className="gap-2">
      <Progress
        className="w-20 bg-fill-2"
        value={total ? (answered / total) * 100 : 0}
        aria-label={`${answered} of ${total} answered`}
      />
      <span className={cn(TABLE_TEXT.muted, "tabular-nums")}>
        {answered}/{total}
      </span>
    </ClientTableCell>
  );
};

export const RequestsListPage: FC<Props> = ({ clientName }) => {
  const { open, openList, listStage, requestMention, requestPrompt } =
    useRequestsIde();
  const data = useRequestsData();
  const newRequest = useNewRequest();
  const { openRequest, openReview } = useOpenRequest();
  const { copyLink, duplicate, saveAsTemplate, remove } = useIntakeActions();
  const filter: RequestsFilter = listStage ?? "all";
  const setFilter = (next: RequestsFilter) =>
    openList({ stage: next === "all" ? null : next, replace: true });
  const [query, setQuery] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<OutgoingIntake | null>(
    null
  );
  const firstName = clientName?.trim().split(/\s+/)[0] || "the client";

  const rows = useMemo(
    () => visibleRequests(data.intakes, filter, query),
    [data.intakes, filter, query]
  );
  const starters = useMemo(
    () => starterPrompts(firstName, data.intakes),
    [data.intakes, firstName]
  );
  const hasRequests = data.intakes.length > 0;
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const clearFilters = () => {
    setQuery("");
    if (filter !== "all") setFilter("all");
  };

  const start = (action: Parameters<typeof newRequest.start>[0]) =>
    void newRequest.start(action);

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col"
      data-testid="requests-list"
    >
      <ClientTableToolbar
        title="Intake Requests"
        search={{
          value: query,
          onChange: setQuery,
          placeholder: "Search requests…",
          ariaLabel: "Search requests"
        }}
        filters={
          <ToolbarFilterPill<RequestsFilter>
            appearance="ghost"
            label="Status"
            ariaLabel="Filter requests by status"
            value={filter}
            options={REQUESTS_FILTERS}
            allOption={{ value: "all", label: "All Requests" }}
            onChange={setFilter}
          />
        }
        onRefresh={() => void data.refetch(["intakes"])}
        isRefreshing={data.isLoading}
        primaryAction={
          <NewRequestMenu
            onNewRequest={start}
            trigger={
              <Button
                variant="primary"
                size="md"
                className="shrink-0"
                aria-label="New Request"
                disabled={newRequest.busy}
                data-testid="new-request"
              >
                <Plus />
                <span className="hidden @md:inline">New Request</span>
              </Button>
            }
          />
        }
      />
      <ClientTable
        ariaLabel="Intake Requests"
        columns={COLUMNS}
        isLoading={data.isLoading}
        isEmpty={rows.length === 0}
        error={data.intakes.length ? null : data.error}
        onRetry={() => void data.refetch(["intakes"])}
        tableClassName="table-fixed"
        empty={
          hasRequests
            ? {
                title: emptyMatchTitle(filter, query),
                icon: ClipboardList,
                message:
                  filter !== "all" && !query.trim()
                    ? "Other requests are under All Requests."
                    : "Try a different status or search.",
                action: (
                  <>
                    <Button
                      variant="secondary"
                      onClick={clearFilters}
                      data-testid="requests-clear-filters"
                    >
                      {filter !== "all" && !query.trim()
                        ? "Show All Requests"
                        : "Clear Filters"}
                    </Button>
                    <NewRequestMenu
                      onNewRequest={start}
                      trigger={
                        <Button
                          variant="ghost"
                          disabled={newRequest.busy}
                          data-testid="requests-empty-new"
                        >
                          <Plus />
                          New Request
                        </Button>
                      }
                    />
                  </>
                )
              }
            : {
                title: "No Requests Yet",
                icon: ClipboardList,
                message: `Send ${firstName} an intake to collect their documents and details.`,
                action: (
                  <>
                    <NewRequestMenu
                      onNewRequest={start}
                      trigger={
                        <Button
                          variant="primary"
                          size="md"
                          disabled={newRequest.busy}
                          data-testid="requests-empty-new"
                        >
                          <Plus />
                          New Request
                        </Button>
                      }
                    />
                    <Button
                      variant="ghost"
                      size="md"
                      onClick={() => setTemplatesOpen(true)}
                      data-testid="requests-empty-template"
                    >
                      <LayoutTemplate />
                      Start from a Template
                    </Button>
                  </>
                ),
                extra: starters.length ? (
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {starters.map((s) => (
                      <Button
                        key={s}
                        variant="pill"
                        size="xs"
                        className="h-auto min-h-6 max-w-full px-2.5 py-0.5 whitespace-normal"
                        onClick={() => requestPrompt(s, { send: true })}
                      >
                        <Sparkles /> {s}
                      </Button>
                    ))}
                  </div>
                ) : undefined
              }
        }
      >
        {rows.map((intake) => {
          const action = rowAction(intake);
          const activity = lastActivity(intake);
          const title = intake.title || "Intake";
          return (
            <ClientTableRow
              key={intake.outgoing_intake_id}
              onActivate={() => openRequest(intake)}
              ariaLabel={`${title}, ${displayStatus(intake)}`}
              data-testid={`request-row-${intake.outgoing_intake_id}`}
            >
              <TitleCell
                title={title}
                subtitle={intake.recipient_email || undefined}
              />
              <ClientTableCell>
                <StatusBadge tone={statusTone(intake)}>
                  {displayStatus(intake)}
                </StatusBadge>
              </ClientTableCell>
              <ProgressCell intake={intake} />
              <MutedCell>{shortDate(intake.sent_at) || "—"}</MutedCell>
              <ClientTableCell className="min-w-0">
                {activity ? (
                  <span
                    className={cn("truncate", TABLE_TEXT.muted)}
                    title={`${activity.label} · ${activity.when}`}
                  >
                    {activity.label} · {activity.when}
                  </span>
                ) : (
                  <span className={TABLE_TEXT.muted}>—</span>
                )}
              </ClientTableCell>
              <ClientTableCell className="justify-end gap-1" data-row-stop>
                {action === "review" ? (
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => void openReview(intake)}
                  >
                    Review
                  </Button>
                ) : null}
                {action === "remind" ? (
                  <RemindPopover intake={intake} clientFirstName={firstName} />
                ) : null}
                {action === "open" ? (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => open(intakeArtifact(intake))}
                  >
                    Open
                  </Button>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`More for ${title}`}
                    >
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem
                      onSelect={() => open(previewArtifact(intake))}
                    >
                      <Eye /> Preview as the Client
                    </DropdownMenuItem>
                    {action === "remind" && intake.has_public_link ? (
                      <DropdownMenuItem onSelect={() => void copyLink(intake)}>
                        <Link2 /> Copy Link
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem onSelect={() => void duplicate(intake)}>
                      <Copy /> Duplicate as Draft
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={!intake.schema?.length}
                      onSelect={() => void saveAsTemplate(intake)}
                    >
                      <LayoutTemplate /> Save as Firm Template
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => requestMention(intakeArtifact(intake))}
                    >
                      <AtSign /> Mention in Chat
                    </DropdownMenuItem>
                    {intake.status === "draft" ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() => setConfirmDelete(intake)}
                        >
                          <Trash2 /> Delete Draft
                        </DropdownMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </ClientTableCell>
            </ClientTableRow>
          );
        })}
      </ClientTable>

      <NewRequestDialog
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        onCreate={start}
      />

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete “{confirmDelete?.title || "Intake"}”?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The draft and its questions are deleted. Nothing was sent to the
              client.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                const intake = confirmDelete;
                setConfirmDelete(null);
                if (intake) void remove(intake);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
